import { auth } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Initialize Cloud Functions
const functions = getFunctions();
const customSendVerification = httpsCallable(functions, 'sendCustomVerificationEmail');
const customSendPasswordReset = httpsCallable(functions, 'sendCustomPasswordResetEmail');

// Send verification email with custom template
export const sendVerificationEmail = async (userOrEmail, displayName = '') => {
  try {
    let email, userId;
    
    // Handle both user object and email string inputs
    if (typeof userOrEmail === 'string') {
      email = userOrEmail;
      await customSendVerification({
        email,
        displayName: displayName || 'User',
        continueUrl: `${window.location.origin}/app/dashboard`
      });
      return true;
    } else {
      // Handle authenticated user flow
      const user = userOrEmail;
      email = user.email;
      userId = user.uid;
      
      // Use our custom verification email
      await customSendVerification({
        email,
        displayName: displayName || user.displayName || 'User',
        continueUrl: `${window.location.origin}/app/dashboard`
      });
      
      // Log the email verification event
      if (userId) {
        await logEmailEvent(userId, 'verification_email_sent', {
          email,
          timestamp: serverTimestamp()
        });
      }
      
      return true;
    }
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};

// Send welcome email using the callable function
// Accepts either a user object or { email, displayName }
export const sendWelcomeEmail = async (user, displayName = '') => {
  try {
    let email, name;
    
    // Handle both user object and direct parameter passing
    if (user && typeof user === 'object' && user.email) {
      email = user.email;
      name = displayName || user.displayName || user.name || 'User';
    } else {
      throw new Error('Invalid user data provided');
    }

    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    
    // Use the correct function name that exists in your Cloud Functions
    const sendWelcomeEmail = httpsCallable(functions, 'sendWelcomeEmailCallable');
    
    await sendWelcomeEmail({
      email,
      displayName: name
    });

    console.log('Welcome email sent successfully');
    return true;
  } catch (error) {
    console.error('Error in sendWelcomeEmail:', error);
    // Don't throw the error to prevent blocking the signup flow
    return false;
  }
};

/**
 * Generic email sending function
 * @param {Object} options - Email options
 * @param {string|string[]} options.to - Recipient email(s)
 * @param {string} options.subject - Email subject
 * @param {string} options.html - HTML content of the email
 * @param {string|string[]} [options.cc] - CC recipient(s) (optional)
 * @param {string|string[]} [options.bcc] - BCC recipient(s) (optional)
 * @param {string} [options.templateId] - Template ID if using templates (optional)
 * @param {Object} [options.templateData] - Data for template replacement (optional)
 * @param {string} [options.eventType] - Event type for logging (e.g., 'welcome_email', 'profile_reminder')
 * @param {string} [options.userId] - User ID for logging (optional)
 * @returns {Promise<Object>} - Result of the email sending operation
 */
export const sendEmail = async ({
  to,
  subject,
  html,
  cc,
  bcc,
  templateId,
  templateData,
  eventType,
  userId
}) => {
  try {
    const functions = getFunctions();
    const sendEmailFunction = httpsCallable(functions, 'sendEmail');
    
    const emailData = {
      to: Array.isArray(to) ? to : [to],
      subject,
      html,
      ...(cc && { cc: Array.isArray(cc) ? cc : [cc] }),
      ...(bcc && { bcc: Array.isArray(bcc) ? bcc : [bcc] }),
      ...(templateId && { templateId }),
      ...(templateData && { templateData })
    };

    const result = await sendEmailFunction(emailData);
    
    // Log the email event if userId and eventType are provided
    if (userId && eventType) {
      try {
        await logEmailEvent(userId, eventType, {
          to,
          subject,
          timestamp: serverTimestamp(),
          ...(cc && { cc }),
          ...(bcc && { bcc })
        });
      } catch (logError) {
        console.error('Error logging email event:', logError);
        // Don't fail the email send if logging fails
      }
    }
    
    return result.data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

// Log email events to Firestore
const logEmailEvent = async (userId, eventType, eventData) => {
  if (!userId) return;
  
  try {
    const eventRef = doc(db, 'emailEvents', `${userId}_${Date.now()}`);
    await setDoc(eventRef, {
      userId,
      eventType,
      ...eventData,
      timestamp: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error logging email event:', error);
    // Don't throw to prevent blocking the main flow
  }
};

// Password reset email
export const sendPasswordResetEmail = async (email) => {
  try {
    await customSendPasswordReset({ email });
    
    // Log the password reset email event
    try {
      const user = auth.currentUser;
      if (user) {
        await logEmailEvent(user.uid, 'password_reset_email_sent', {
          email,
          timestamp: serverTimestamp()
        });
      }
    } catch (logError) {
      console.error('Error logging password reset email:', logError);
      // Don't fail the operation if logging fails
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};
